/* Copyright (c) 2016-2024 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import validate from "../util/validate.js";
import fieldManager from "../util/fields.js";
import util from "../util/util.js";

function plantNames(site) {
	if(site && site.metadata && site.metadata.flowering_species) {
		return site.metadata.flowering_species.split(",").map(x => x.trim()).filter(x => x);
	} else {
		return [];
	}
}

function isBrunyIslandSurvey(survey) {
	return survey?.sharedSite?.()?.metadata?.region == 'Tasmania - South'
}

var program = {
	code: "swp",
	name: "Swift Parrot Search",
	description: "<p>“Swift Parrot Search” is a comprehensive citizen science monitoring project designed to address pressing knowledge gaps for this critically endangered species on mainland Australia.  The project will also benefit many other threatened and declining woodland birds such as the Regent Honeyeater.</p>" +
		"<p>The migratory Swift Parrot <i>Lathamus discolor</i> is on the brink of extinction in the wild. Although many of the challenges and opportunities for the species’ recovery are in its Tasmanian breeding habitat, there is also much that can and is being done in its wintering grounds on mainland Australia. This monitoring program is one of these.</p>" +
		"<p>Launched in 2021, “Swift Parrot Search” is an updated approach to the volunteer-based monitoring that has been undertaken biannually for the species since the mid-1990s.  Swift Parrot Search will have more than 2500 fixed monitoring sites in known and potentially suitable habitat on public land.  Using the 5 minute-50 metre radius survey method, each of these fixed sites is ideally surveyed at least once during each of the two following periods:</p>" +
		"<ul>" +
		"<li>From last week in April to first week in June (roughly centred on the traditional 3rd weekend in May)</li>" +
		"<li>From mid-July to late August (roughly centred on the traditional 1st weekend in August).</li>" +
		"</ul>" +
		"<p>All bird species detected during a survey at these fixed monitoring sites are to be recorded regardless of there being Swift Parrots present or not.  Documentation of flowering intensity and water availability at the time of each visit is also encouraged.</p>" +
		"<p>For all other types of woodland bird searches, including incidental and opportunistic sightings of Swift Parrots and other threatened species away from these fixed survey points, use the 'General Birdata Survey' option.</p>" +
		"<p><b>Project contact:</b> <a href='mailto:woodlandbirds@birdlife.org.au'>woodlandbirds@birdlife.org.au</a><br>" +
		"<b>Project webpage:</b>  <a href='https://birdlife.org.au/swift-parrot' target='_blank'>https://birdlife.org.au/swift-parrot</a></p>",
	load: function() {
		return Promise.all([birdata.lookup()]).then(function([lookups]) {
			program.options = lookups;
			program.plantNames = ko.observable([]);

			// This takes ages to load, so we do it in the background
			birdata.sharedSites({ layer: "swp" }).then(function(sharedSites) {
				// TODO: Maybe we should just get the server to provide this list anyway?
				var names = new Set();
				sharedSites.forEach(site => {
					plantNames(site).forEach(name => {
						names.add(name);
					});
				});
				var nameList = [];
				names.forEach(x => nameList.push(x));
				program.plantNames(nameList.sort());
			});

			return true;
		});
	},
	fields: function(survey) {
		let isBruny = ko.pureComputed(() => fields.subprogram() == 'bruny_island');
		let plantsDescription = ko.pureComputed(() =>
			isBruny() ? 'eucalypt and banksia' : 'tree and mistletoe');

		function validateRequiredIfBruny(field) {
			if(isBruny() && util.isEmptyValue(field.value())) {
				return field.label + " is required";
			}
		}

		var fields = [
			{
				label: "Is lerp present within the search area?",
				name: "lerpStatus",
				type: "text",
				options: ["Present – Light/Moderate", "Present – Heavy", "Unsure", "Absent"],
				help: `
				<p style = 'margin-bottom: 1em;'>’Lerp’ are the sugar-rich casings attached to eucalypt leaves by sap-sucking insects called ‘psyllids’. These casings are white in colour and up to 5mm in size, and are made up of solidified honeydew and wax.</p>
				<ul>
					<li><b>Light/Moderate</b>: The foliage of most or all trees contain only minimal to moderate amounts of lerp – OR – the foliage of most or all trees do not contain lerp at all, with a small proportion of existing trees containing light, moderate or even heavy amounts of lerp.</li>
					<li><b>Heavy</b>: Lerp is abundantly available, with the foliage of most or all trees containing a heavy amount – OR – the foliage of most or all trees contain a light to moderate amount of lerp, with a small proportion of existing trees containing heavy amount of lerp.</li>
					<li><b>Unsure</b>: Survey conditions (e.g. weather, available light etc.) made it difficult to detect lerp – OR – the observer is unfamiliar with identifying lerp.</li>
					<li><b>Absent</b>: No trees within the search area were seen to bear lerp among their foliage.</li>
				</ul>`,
				validate: validateRequiredIfBruny,
				required: isBruny
			},
			{
				name: "plants",
				type: "compound",
				label: "Flowering intensity within search area",
				help: ko.pureComputed(() => `
				<p style='margin-bottom: 1em;'>Based on conditions at the time of survey, provide overall flowering intensity for each known ${plantsDescription()} species present.</p>
				<ul>
					<li><b>None</b>: No plants of this species within the search area have any flowers at all.</li>
					<li><b>Light</b>: Most or all plants of this species have a small number of flowers – OR - most or all plants of this species have no flowers at all, but a small proportion are covered in a few or many flowers (1-20% flowering potential).</li>
					<li><b>Moderate</b>: Most or all plants of this species have a moderate amount of flowering – OR - many plants of this species have little or no flowering, but there is higher level of flowering among a prominent subset of plants (20-50% flowering potential).</li>
					<li><b>Heavy</b>: Most plants of this species are covered in flowers at 50-80% of their full flowering potential.</li>
					<li><b>Very Heavy</b>: Most or all of the plants of this species are flowering at 80% or more of their full flowering capacity.</li>
					<li><b>Not detected</b>: Unable to detect this species within the search area.</li>
					<li><b>Unsure if flowering</b>: Unable to confirm the flowering status of this species.</li>
				</ul>`),
				minCount: 0,
				maxCount: 99,
				fields: [
					{
						name: "plantName",
						type: "text",
						options: program.plantNames,
						validate: function(field) {
							if(field.value()) {
								var plants = fields.plants();

								var i = 0;
								for(i = 0; i < plants.length; i++) {
									if(plants[i].plantName.field === field) {
										return;
									} else if(plants[i].plantName() == field.value()) {
										var name = field.value();
										return {
											brief: "There is already an entry above for " + name,
											full: "Plant repeated (" + name + ")"
										};
									}
								}
							}
						}
					},
					{
						name: "floweringIntensity",
						type: "options",
						options: program.options.flowering_intensity
					}
				]
			},
			{
				label: "What is the status of Golden Wattle (A. pycnantha) within the search area?",
				name: "goldenWattleStatus",
				type: "text",
				options: [
					"Not flowering",
					"Lightly flowering",
					"Moderately flowering",
					"Heavily flowering",
					"Very heavily flowering",
					"Unsure if occurring",
					"Absent"
				],
				help: `<ul>
					<li><b>Not flowering</b>: No plants within the search area have any flowers at all.</li>
					<li><b>Lightly flowering</b>: Most or all plants have a small number of flowers – OR - most or all plants have no flowers at all, but a small proportion are covered in a few or many flowers (1-20% flowering potential).</li>
					<li><b>Moderately flowering</b>: Most or all plants have a moderate amount of flowering – OR - many plants have little or no flowering, but there is higher level of flowering among a prominent subset of plants (20-50% flowering potential).</li>
					<li><b>Heavily flowering</b>: Most plants are covered in flowers at 50-80% of their full flowering potential.</li>
					<li><b>Very heavily flowering</b>: Most or all of the plants are flowering at 80% or more of their full flowering capacity.</li>
					<li><b>Unsure if occurring</b>: Unable to confirm if present or absent.</li>
					<li><b>Absent</b>: Confirmed not to occur within the search area.</li>
				</ul>`,
				hidden: isBruny
			},
			{
				label: "What is the availability of ripe fruits among Native Cherry (Exocarpos cupressiformis) within the search area?",
				name: "nativeCherryStatus",
				type: "text",
				options: [
					"None",
					"Light",
					"Moderate",
					"Heavy",
					"Unsure if occurring",
					"Not detected"
				],
				help: `
				<p style = 'margin-bottom: 1em;'>Native Cherry <i>(Exocarpos cupressiformis)</i> is a root-hemiparasitic plant that stands up to 8 metres tall and appears similar in form to a conifer, with drooping foliage ranging from gold to green or dark green in colour and rough dark brown/black bark. Their small cream flowers are inconspicuous, and are followed by the production of fleshy, sugar-rich fruits that are globular in shape, typically 5mm in diameter and appear red when ripe.</p>
				<ul>
					<li><b>None</b>: No plants within the search area bear any ripe fruits at all.</li>
					<li><b>Light</b>: Most or all plants bear a small number of ripe fruits - Or - Most or all plants bear no ripe fruits at all, but a small proportion are covered in a few or many ripe fruits (1-35% fruiting potential across the site).</li>
					<li><b>Moderate</b>: Most or all plants bear a moderate amount of ripe fruits - or - Many plants have little or no ripe fruits, but there is higher level of ripe fruit availability among a prominent subset of plants (36-70% fruiting potential across the site).</li>
					<li><b>Heavy</b>: Most plants are covered in ripe fruits at more than 70% of their full fruiting potential.</li>
					<li><b>Unsure if occurring</b>: Unable to confirm if Native Cherry is present or absent.</li>
					<li><b>Not detected</b>: Unable to detect Native Cherry within the search area.</li>
				</ul>`,
				hidden: () => !isBruny(),
				required: isBruny,
				validate: validateRequiredIfBruny
			},
			{
				name: "freshwaterPresent",
				help: "Is there any freshwater present within the search area at the time of survey?",
				label: "Is freshwater present within search area?",
				type: "boolean",
				allowNull: true,
				optionLabels: ["Yes", "No", "Select…"],
				defaultValue: null,
				required: isBruny,
				validate: validateRequiredIfBruny
			},
			{
				name: "breedingObservationType",
				label: "Any Swift Parrot breeding behaviour observed within 300m of the site?",
				help: `
				<p>What types of behaviour may be indicative of breeding?</p>
				<ul>
					<li><b>Diagnostic behaviour</b>: Behaviour confirming an active nesting attempt. Food repeatedly carried to hollow entrance, young seen or heard begging from within the cavity etc.</li>
					<li><b>Suggestive behaviour</b>: Behaviour suggesting a nesting attempt, but evidence insufficient to be used as confirmation. Hollow inspection, observed courtship/copulation etc.</li>
				</ul>
				<p>Note: You may return to answering this question following your survey and habitat assessment.</p>
				`,
				type: "options",
				options: program.options.swp_breeding_observation_type,
				hidden: () => !isBruny(),
				required: isBruny,
				validate: validateRequiredIfBruny
			},
			{
				name: "temperatureCelsius",
				type: "number",
				label: "Approximate temperature",
				units: "°C",
				max: 80,
				min: -30,
				decimalPlaces: 1
			},
			{
				name: "windSpeed",
				label: "Average wind speed",
				type: "options",
				style: "radio",
				options: program.options.bof_wind_speed
			},
			{
				name: "subprogram",
				type: "text",
				hidden: () => true
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	programData: function(survey) {
		var fields = program.fields(survey);

		let subprogram = ko.pureComputed(() => isBrunyIslandSurvey(survey) ? 'bruny_island' : 'mainland');
		subprogram.subscribe(fields.subprogram);

		function updatePlantFields() {
			if(survey.program() === program) {
				var sp = survey.surveyPoint();
				if(sp?.sharedSite?.metadata?.flowering_species) {
					var newSpecies = sp.sharedSite.metadata.flowering_species
						.trim().split(/\s*,\s*/).filter(x => x.trim().length > 0);
					var existingPlants = fields.plants();

					var newPlants = newSpecies.map(function(plantName) {
						var item = existingPlants.find(x => x.plantName() == plantName);
						if(item == null) {
							item = fields.plants.field.createItem();
							item.plantName(plantName);
						} else {
							item = { ...item };
						}
						item.canEditPlantName = false;
						return item;
					});

					existingPlants.forEach(function(item) {
						if(item.plantName() &&
							!newSpecies.includes(item.plantName()) &&
							(item.floweringIntensity() || item.canEditPlantName)) {
							newPlants.push({ ...item, canEditPlantName: true });
						}
					});

					fields.plants(newPlants);
				}
			}
		}

		survey.surveyPoint.subscribe(updatePlantFields);
		survey.program.subscribe(updatePlantFields);

		return {
			fields: fields,
			validations: ko.pureComputed(function() {
				// This is dynamic so that we can validate nested plant fields
				return Object.values(fields)
					.flatMap(f => f.field.withDescendants())
					.map(f => f.value);
			}),
			subprogram: subprogram
		};
	},
	visibleSharedSiteLayers: () => ["swp"],
	defaultAreaLayer: (layers) => null,
	multipleDaysEnabled: function() { return false; },
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().swp.fields;
		json.swp = {};

		for(let f of Object.values(fields)) {
			f.field.save(json.swp);
		}

		json.swp.plants = json.swp.plants.filter(function(plant) {
			return plant.plant_name && plant.flowering_intensity;
		});

		delete json.swp.subprogram;
	},
	hideWaterLevel: true,
	hideSurveyType: true,
	surveyTypeOptions: [],
	durationEnabled: function(survey) {
		return false;
	},
	afterSelect: function(survey) {
		// Set survey type after program selected
		survey.type({
			code: '5_min_point_search',
			name: '5 minute point search'
		});
		survey.durationMinutes(5);
        survey.durationHours(0);
        survey.multipleDays(false);
		// survey.allSpeciesRecorded(false);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.swp;

		if(!data) return; //incomplete survey

		for(let f of Object.values(fields)) {
			f.field.load(data);
		}

		if(survey == null) {
			// Currently we are using the presence of the native cherry field to
			// detect that this survey is a BI survey. Perhaps we should record
			// subprogram in the backend instead.
			// Note: this logic is only used for showing survey summaries.
			fields.subprogram(json.swp.native_cherry_status ? 'bruny_island' : 'mainland');
		}
	},
	siteBasedEntry: true,
	forcePrivate: function(survey) {
		return survey.programData().swp?.subprogram() == 'bruny_island';
	}
};

export default program;
